import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  meetings,
  type BreakoutAssignment,
  type BreakoutMove,
  type BreakoutRoomSpec,
  type BreakoutSession,
  type MeetingPanelEventPayload,
} from "services/api/meetingsClient"
import type { NativeBreakoutObservation } from "mobile/useNativeBreakoutContext"
import type { CallSurfaceAttendee, JoinInfo } from "../types"

/** Fallback poll while a call is live, in case a realtime event is missed. */
export const BREAKOUT_POLL_MS = 20000

export type BreakoutLocation = { kind: "main" } | { kind: "room"; roomId: string; roomName: string }

/** Prebuilt's "End in 60 seconds". */
export const BREAKOUT_END_DELAY_SECONDS = 60

export type BreakoutRoomsState = {
  session: BreakoutSession | null
  location: BreakoutLocation
  /** Epoch ms when a scheduled end fires, or null. */
  endsAt: number | null
  /** An assignment the attendee has not joined yet (join is manual, or the host chose to stay). */
  pendingInvite: (Pick<BreakoutAssignment, "room_id" | "room_name"> & Partial<Pick<BreakoutAssignment, "url" | "token">>) | null
  busy: boolean
  nativePresence?: boolean
  presenceVerified?: boolean
  returnError?: string | null
  error: string | null
  isHost: boolean
  refresh: () => Promise<void>
  joinPendingInvite: () => Promise<void>
  joinRoomAsHost: (roomId: string) => Promise<void>
  returnToMain: () => Promise<void>
  startSession: (rooms: BreakoutRoomSpec[], autoJoin: boolean, mentorshipVersion?: string) => Promise<boolean>
  moveMembers: (moves: BreakoutMove[]) => Promise<boolean>
  broadcast: (message: string) => Promise<boolean>
  /** End now (0) or after a countdown everyone can see. */
  endSession: (delaySeconds?: number) => Promise<boolean>
}

type UseBreakoutRoomsArgs = {
  meetingId: string
  attendee: CallSurfaceAttendee
  joinInfo: JoinInfo
  enabled: boolean
  isHost: boolean
  subscribeMeetingEvents?: (listener: (event: MeetingPanelEventPayload) => void) => () => void
  switchRoom: (target: { url: string; token?: string | null; label: string }) => Promise<boolean>
  /** Tell the lifecycle where to go if the breakout room disappears (null while in the main room). */
  setRejoinTarget?: (target: { url: string; token?: string | null; label: string } | null) => void
  onNotice: (message: string) => void
  admissionToken?: string
  nativeObservation?: NativeBreakoutObservation
  nativeRoomTarget?: (roomId: string) => Promise<BreakoutAssignment>
  mainRoomRecoveryVersion?: number
}

const describeError = (err: any, fallback: string) => err?.response?.data?.detail || err?.message || fallback

/**
 * WiseFriend breakout rooms for the custom call. The host starts a session through the API,
 * every attendee learns about it over the meeting's realtime events (or the fallback poll),
 * fetches their own room and token, and moves there with the same call object. Hosts stay in
 * the main room unless they hop into a room or explicitly assigned themselves to one, and can
 * bring everyone back.
 */
export function useBreakoutRooms({ meetingId, attendee, joinInfo, enabled, isHost, subscribeMeetingEvents, switchRoom, setRejoinTarget, onNotice, admissionToken, mainRoomRecoveryVersion = 0, nativeObservation, nativeRoomTarget }: UseBreakoutRoomsArgs): BreakoutRoomsState {
  const [session, setSessionState] = useState<BreakoutSession | null>(null)
  const [location, setLocation] = useState<BreakoutLocation>({ kind: "main" })
  const [pendingInvite, setPendingInvite] = useState<(Pick<BreakoutAssignment, "room_id" | "room_name"> & Partial<Pick<BreakoutAssignment, "url" | "token">>) | null>(null)
  const [busy, setBusy] = useState(false)
  const [returnFailure, setReturnFailure] = useState<{ scope: string; message: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [movementFailure, setMovementFailure] = useState<{ scope: string; message: string } | null>(null)
  const locationRef = useRef<BreakoutLocation>(location)
  locationRef.current = location
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled
  const isHostRef = useRef(isHost)
  isHostRef.current = isHost
  const switchRoomRef = useRef(switchRoom)
  switchRoomRef.current = switchRoom
  const onNoticeRef = useRef(onNotice)
  onNoticeRef.current = onNotice
  const attendeeRef = useRef(attendee)
  attendeeRef.current = attendee
  const joinInfoRef = useRef(joinInfo)
  joinInfoRef.current = joinInfo
  const movingRef = useRef(false)
  const sessionRef = useRef<BreakoutSession | null>(null)
  const hostRequestRef = useRef(0)
  const hostLookupPendingRef = useRef(false)
  const inviteRequestRef = useRef(0)
  const inviteLookupPendingRef = useRef(false)
  const cancelInviteLookup = useCallback(() => {
    inviteRequestRef.current += 1
    if (inviteLookupPendingRef.current) {
      inviteLookupPendingRef.current = false
      setBusy(false)
    }
  }, [])
  const cancelHostLookup = useCallback(() => {
    hostRequestRef.current += 1
    if (hostLookupPendingRef.current) {
      hostLookupPendingRef.current = false
      setBusy(false)
    }
  }, [])
  const setSession = useCallback((next: BreakoutSession | null) => {
    if (next?.id !== sessionRef.current?.id || next?.status !== "active") {
      cancelHostLookup()
      cancelInviteLookup()
    }
    sessionRef.current = next
    setSessionState(next)
  }, [cancelHostLookup, cancelInviteLookup])
  const returnRequestedRef = useRef(false)
  const refreshRequestRef = useRef(0)
  const refreshQueuedRef = useRef(false)
  const refreshRef = useRef<() => Promise<void>>(async () => {})
  const previousRecoveryRef = useRef(mainRoomRecoveryVersion)
  /** Bumped whenever a session is known to have ended, so in-flight responses from before are dropped. */
  const epochRef = useRef(0)
  const nativeRef = useRef(nativeObservation)
  const nativeTargetRef = useRef(nativeRoomTarget)
  nativeTargetRef.current = nativeRoomTarget
  // Invalidate captured async actions synchronously, including batched native events.
  if (nativeRef.current?.scope !== nativeObservation?.scope) {
    epochRef.current += 1
    hostRequestRef.current += 1
    inviteRequestRef.current += 1
    refreshRequestRef.current += 1
    movingRef.current = false
    returnRequestedRef.current = false
  }
  nativeRef.current = nativeObservation
  const nativeAllowed = useCallback(() => !nativeRef.current || (nativeRef.current.status === 'verified' && nativeRef.current.isCurrent()), [])
  const nativeActionCurrent = useCallback(() => !nativeRef.current || nativeRef.current.isCurrent(), [])
  useEffect(() => {
    if (!nativeRef.current) return
    if (!nativeRef.current.isCurrent()) {
      setBusy(false)
      cancelHostLookup()
      cancelInviteLookup()
    }
    const context = nativeRef.current.context
    if (!context) {
      setSession(null)
      setPendingInvite(null)
      return
    }
    locationRef.current = context.status === 'breakout' && context.room
      ? { kind: 'room', roomId: context.room.id, roomName: context.room.name } : { kind: 'main' }
    setLocation(locationRef.current)
    sessionRef.current = context.session
    setSessionState(context.session)
    hostAutoJoinedSessionRef.current = context.session?.id || null
    setPendingInvite(context.assignment && (locationRef.current.kind !== 'room' || context.assignment.room_id !== locationRef.current.roomId)
      ? context.assignment : null)
  }, [nativeObservation?.scope, nativeObservation?.status, nativeObservation?.context, cancelHostLookup, cancelInviteLookup, setSession])
  const endedSessionIdsRef = useRef<Set<string>>(new Set())
  /** The session in which the host has already been carried into their own room automatically. */
  const hostAutoJoinedSessionRef = useRef<string | null>(null)
  const setRejoinTargetRef = useRef(setRejoinTarget)
  setRejoinTargetRef.current = setRejoinTarget
  useEffect(() => {
    if (nativeRef.current || previousRecoveryRef.current === mainRoomRecoveryVersion) return
    previousRecoveryRef.current = mainRoomRecoveryVersion
    locationRef.current = { kind: "main" }
    setLocation(locationRef.current)
    setPendingInvite(null)
    setRejoinTargetRef.current?.(null)
  }, [mainRoomRecoveryVersion])
  useEffect(() => {
    if (!enabled || nativeRef.current) return
    const info = joinInfoRef.current
    setRejoinTargetRef.current?.(location.kind === "room" ? { url: info.url, token: info.token, label: "the main room" } : null)
  }, [enabled, location])

  const admissionTokenRef = useRef(admissionToken)
  admissionTokenRef.current = admissionToken
  const fetchCurrent = useCallback(async (roomId?: string) => {
    const current = attendeeRef.current
    if (current.isGuest) {
      if (!current.guestToken || !current.breakoutToken) return { session: null, assignment: null }
      return meetings.fetchGuestBreakoutCurrent(meetingId, { guest_token: current.guestToken, breakout_token: current.breakoutToken, display_name: current.displayName, ...(admissionTokenRef.current ? { admission_token: admissionTokenRef.current } : {}) })
    }
    return meetings.fetchBreakoutCurrent(meetingId, roomId ? { room_id: roomId } : {})
  }, [meetingId])

  const moveTo = useCallback(async (assignment: BreakoutAssignment) => {
    if (movingRef.current || !nativeActionCurrent()) return false
    const nativeScope = nativeRef.current?.scope
    movingRef.current = true
    // A response requested before this move cannot describe its new location.
    refreshRequestRef.current += 1
    const epoch = epochRef.current
    setBusy(true)
    try {
      const ok = await switchRoomRef.current({ url: assignment.url, token: assignment.token, label: assignment.room_name })
      if (nativeRef.current) {
        if (!ok && nativeRef.current.scope === nativeScope && nativeActionCurrent()) setMovementFailure({ scope: nativeScope!, message: 'Unable to join that room. Please try again.' });
        return ok // Command acknowledgment is never joined presence.
      }
      if (ok) {
        locationRef.current = { kind: "room", roomId: assignment.room_id, roomName: assignment.room_name }
        setLocation(locationRef.current)
        // An end event may arrive while Daily is still joining. Finish returning immediately,
        // rather than dropping the event and leaving the attendee in a closed session.
        if (epoch !== epochRef.current || returnRequestedRef.current) {
          returnRequestedRef.current = false
          const info = joinInfoRef.current
          if (await switchRoomRef.current({ url: info.url, token: info.token, label: "the main room" })) {
            locationRef.current = { kind: "main" }
            setLocation(locationRef.current)
            setPendingInvite(null)
          }
          return false
        }
        setPendingInvite(null)
        onNoticeRef.current(`You're in ${assignment.room_name}.`)
      } else if (epoch === epochRef.current && !returnRequestedRef.current) {
        setPendingInvite(assignment)
        setError(`Unable to join ${assignment.room_name}. Please try again.`)
      }
      return ok
    } finally {
      if (!nativeRef.current || nativeRef.current.scope === nativeScope) {
      movingRef.current = false
      returnRequestedRef.current = false
      setBusy(false)
      if (refreshQueuedRef.current) {
        refreshQueuedRef.current = false
        void refreshRef.current()
      }
      }
    }
  }, [nativeActionCurrent])

  const returnToMain = useCallback(async () => {
    if (!nativeAllowed()) return
    // A pending host-room lookup is an intention, not a completed move. Cancel it even
    // when already in main, so its eventual response cannot undo an explicit return.
    cancelHostLookup()
    cancelInviteLookup()
    if (movingRef.current) {
      returnRequestedRef.current = true
      return
    }
    if (nativeRef.current ? nativeRef.current.context?.status === "main" : locationRef.current.kind === "main") {
      setPendingInvite(null)
      return
    }
    const nativeScope = nativeRef.current?.scope
    setReturnFailure(null)
    movingRef.current = true
    setBusy(true)
    try {
      const info = joinInfoRef.current
      const ok = await switchRoomRef.current({ url: info.url, token: info.token, label: "the main room" })
      if (nativeRef.current) {
        if (!ok && nativeRef.current.scope === nativeScope && nativeRef.current.isCurrent())
          setReturnFailure({ scope: nativeScope!, message: "Unable to return to the main room. Please try again or leave the call." })
        return
      }
      if (ok) {
        locationRef.current = { kind: "main" }
        setLocation(locationRef.current)
        setPendingInvite(null)
        onNoticeRef.current("You're back in the main room.")
      } else {
        setError("Unable to return to the main room. Please try again.")
      }
    } finally {
      if (!nativeRef.current || nativeRef.current.scope === nativeScope) {
      movingRef.current = false
      returnRequestedRef.current = false
      setBusy(false)
      if (refreshQueuedRef.current) {
        refreshQueuedRef.current = false
        void refreshRef.current()
      }
      }
    }
  }, [cancelHostLookup, cancelInviteLookup, nativeAllowed])

  /** Reconcile the attendee's place against the server: move, invite, or return as needed. */
  const refresh = useCallback(async () => {
    if (!enabledRef.current) return
    if (nativeRef.current) { await nativeRef.current.refresh(); return }
    if (movingRef.current || inviteLookupPendingRef.current) {
      refreshQueuedRef.current = true
      return
    }
    const epoch = epochRef.current
    const request = ++refreshRequestRef.current
    let payload: { session: BreakoutSession | null; assignment: BreakoutAssignment | null }
    try {
      payload = await fetchCurrent()
    } catch (err: any) {
      if (request !== refreshRequestRef.current || epoch !== epochRef.current || !enabledRef.current) return
      setError(describeError(err, "Unable to load breakout rooms."))
      return
    }
    // A session ended while this request was in flight: its answer is stale.
    if (epoch !== epochRef.current || request !== refreshRequestRef.current || !enabledRef.current) return
    if (movingRef.current) {
      refreshQueuedRef.current = true
      return
    }
    if (payload.session && endedSessionIdsRef.current.has(payload.session.id)) {
      payload = { session: null, assignment: null }
    }
    setError(null)
    setSession(payload.session)
    const current = locationRef.current
    if (!payload.session || payload.session.status !== "active") {
      setPendingInvite(null)
      if (current.kind === "room") await returnToMain()
      return
    }
    const assignment = payload.assignment
    if (assignment) {
      if (current.kind === "room" && current.roomId === assignment.room_id) {
        setPendingInvite(null)
        return
      }
      // Attendees follow the session's auto-join setting. A host is carried in once, and only
      // when they explicitly placed themselves in a room ("Include me in my mentor room");
      // after that they decide when to visit or return.
      const hostFollows = isHostRef.current && hostAutoJoinedSessionRef.current !== payload.session.id
      if (payload.session.auto_join && (!isHostRef.current || hostFollows)) {
        const moved = await moveTo(assignment)
        if (moved && isHostRef.current) hostAutoJoinedSessionRef.current = payload.session.id
      } else {
        setPendingInvite(assignment)
      }
      return
    }
    setPendingInvite(null)
    if (current.kind === "room" && !isHostRef.current) await returnToMain()
  }, [fetchCurrent, moveTo, returnToMain, setSession])

  refreshRef.current = refresh

  // Realtime events, plus a poll as a safety net.
  useEffect(() => {
    if (!enabled) return
    if (!nativeRef.current) void refreshRef.current()
    const interval = window.setInterval(() => {
      if (nativeRef.current && !['verified', 'returning'].includes(nativeRef.current.status)) return
      void refreshRef.current()
    }, BREAKOUT_POLL_MS)
    const unsubscribe = subscribeMeetingEvents?.((event) => {
      const type = String(event?.type || "")
      if (nativeRef.current && type === 'meeting.updates_reconnected') {
        void nativeRef.current.refresh()
        onNoticeRef.current('Meeting updates reconnected. Ask the host to repeat any announcements you may have missed.')
        return
      }
      if (nativeRef.current && type.startsWith("meeting.breakout.") && type !== "meeting.breakout.message") {
        void nativeRef.current.refresh()
        return
      }
      if (!type.startsWith("meeting.breakout.")) return
      if (type === "meeting.breakout.message") {
        const message = typeof event.message === "string" ? event.message : ""
        const sender = typeof event.sender_name === "string" && event.sender_name ? event.sender_name : "Host"
        if (message) onNoticeRef.current(`${sender}: ${message}`)
        return
      }
      if (type === "meeting.breakout.ending") {
        void refreshRef.current()
        return
      }
      if (type === "meeting.breakout.ended") {
        epochRef.current += 1
        const endedId = (event.session as { id?: string } | undefined)?.id
        if (endedId) endedSessionIdsRef.current.add(endedId)
        setSession(null)
        setPendingInvite(null)
        if (locationRef.current.kind === "room" || movingRef.current) void returnToMain()
        return
      }
      void refreshRef.current()
    })
    return () => {
      epochRef.current += 1
      refreshRequestRef.current += 1
      cancelInviteLookup()
      refreshQueuedRef.current = false
      window.clearInterval(interval)
      unsubscribe?.()
    }
  }, [cancelInviteLookup, enabled, meetingId, returnToMain, setSession, subscribeMeetingEvents])

  const joinPendingInvite = useCallback(async () => {
    const invitation = nativeRef.current ? nativeRef.current.context?.assignment : pendingInvite
    if (!nativeAllowed() || !enabledRef.current || !invitation || movingRef.current || inviteLookupPendingRef.current) return
    const epoch = epochRef.current
    const request = ++inviteRequestRef.current
    const clickedSessionId = nativeRef.current ? nativeRef.current.context?.session?.id : session?.id
    const isCurrent = () => nativeActionCurrent() && epoch === epochRef.current && request === inviteRequestRef.current && enabledRef.current
    // Accepting an invite owns this lookup. Older background replies must not overwrite it,
    // and later refresh requests are queued rather than silently cancelling the click.
    refreshRequestRef.current += 1
    inviteLookupPendingRef.current = true
    refreshQueuedRef.current = false
    setBusy(true)
    setError(null)
    setMovementFailure(null)
    try {
      // Background tabs can suspend polling beyond a room token's lifetime. Validate the
      // invitation and obtain a fresh token before accepting what the attendee clicked.
      if (nativeRef.current) {
        const assignment = await nativeTargetRef.current!(invitation.room_id)
        if (isCurrent() && assignment.room_id === invitation.room_id) await moveTo(assignment)
        return
      }
      let payload = await fetchCurrent()
      if (!isCurrent()) return
      // An assignment event or poll during the lookup needs a newer answer. Bound retries
      // so a stream of updates produces a visible retry instead of an indefinitely busy UI.
      for (let attempt = 0; refreshQueuedRef.current && attempt < 3; attempt += 1) {
        refreshQueuedRef.current = false
        payload = await fetchCurrent()
        if (!isCurrent()) return
      }
      if (refreshQueuedRef.current) {
        refreshQueuedRef.current = false
        setError("Your breakout assignment is still updating. Please try joining again.")
        return
      }
      if (!payload.session || payload.session.status !== "active" || endedSessionIdsRef.current.has(payload.session.id)) {
        setSession(null)
        setPendingInvite(null)
        if (locationRef.current.kind === "room") await returnToMain()
        return
      }
      setSession(payload.session)
      if (!payload.assignment) {
        setPendingInvite(null)
        if (locationRef.current.kind === "room" && !isHostRef.current) await returnToMain()
        return
      }
      if (payload.assignment.room_id !== invitation.room_id || payload.session.id !== clickedSessionId) {
        setPendingInvite(payload.assignment)
        onNoticeRef.current(`Your assignment changed to ${payload.assignment.room_name}. Join when you're ready.`)
        return
      }
      inviteLookupPendingRef.current = false
      await moveTo(payload.assignment)
    } catch (err: any) {
      if (isCurrent()) {
        if (nativeRef.current) setMovementFailure({ scope: nativeRef.current.scope, message: 'Unable to join that room. Please try again.' })
        else setError(describeError(err, "Unable to check your breakout room. Please try again."))
      }
    } finally {
      if (request === inviteRequestRef.current) {
        inviteLookupPendingRef.current = false
        refreshQueuedRef.current = false
        setBusy(false)
      }
    }
  }, [nativeAllowed, nativeActionCurrent, fetchCurrent, moveTo, pendingInvite, returnToMain, session?.id, setSession])

  const joinRoomAsHost = useCallback(async (roomId: string) => {
    const currentSession = nativeRef.current ? nativeRef.current.context?.session : sessionRef.current
    if (!nativeAllowed() || !enabledRef.current || !isHostRef.current || movingRef.current || currentSession?.status !== "active") return
    const epoch = epochRef.current
    const request = ++hostRequestRef.current
    // The explicit visit supersedes older main-room polls. Newer authoritative
    // refreshes/end events may still cancel the lookup below.
    refreshRequestRef.current += 1
    const isCurrent = () => nativeActionCurrent() && request === hostRequestRef.current && epoch === epochRef.current &&
      enabledRef.current && isHostRef.current && (nativeRef.current?.context?.session || sessionRef.current)?.id === currentSession.id &&
      (nativeRef.current?.context?.session || sessionRef.current)?.status === "active" && !endedSessionIdsRef.current.has(currentSession.id)
    hostLookupPendingRef.current = true
    setMovementFailure(null)
    setBusy(true)
    setError(null)
    try {
      if (nativeRef.current) {
        const assignment = await nativeTargetRef.current!(roomId)
        if (isCurrent() && assignment.room_id === roomId) await moveTo(assignment)
        return
      }
      const payload = await fetchCurrent(roomId)
      if (!isCurrent() || payload.session?.id !== currentSession.id || payload.session.status !== "active" || payload.assignment?.room_id !== roomId) return
      hostLookupPendingRef.current = false
      if (payload.assignment) await moveTo(payload.assignment)
    } catch (err: any) {
      if (isCurrent()) {
        if (nativeRef.current) setMovementFailure({ scope: nativeRef.current.scope, message: 'Unable to join that room. Please try again.' })
        else setError(describeError(err, "Unable to join that room."))
      }
    } finally {
      if (request === hostRequestRef.current) {
        hostLookupPendingRef.current = false
        setBusy(false)
      }
    }
  }, [nativeAllowed, nativeActionCurrent, fetchCurrent, moveTo])

  const run = useCallback(async (work: (isCurrent: () => boolean) => Promise<unknown>, fallback: string) => {
    if (!nativeAllowed() || (nativeRef.current && !isHostRef.current)) return false
    const observation = nativeRef.current
    const isCurrent = () => !observation || (nativeRef.current?.scope === observation.scope && observation.isCurrent())
    setBusy(true)
    setError(null)
    try {
      await work(isCurrent)
      return isCurrent()
    } catch (err: any) {
      if (isCurrent()) setError(describeError(err, fallback))
      return false
    } finally {
      if (isCurrent()) setBusy(false)
    }
  }, [nativeAllowed])

  const startSession = useCallback((rooms: BreakoutRoomSpec[], autoJoin: boolean, mentorshipVersion?: string) =>
    run(async isCurrent => {
      const payload = await meetings.createBreakoutSession(meetingId, { auto_join: autoJoin, rooms, ...(mentorshipVersion ? { mentorship_version: mentorshipVersion } : {}) })
      if (!isCurrent()) return
      if (!nativeRef.current) setSession(payload.session)
      await refreshRef.current()
    }, "Unable to start breakout rooms."), [meetingId, run, setSession])

  const moveMembers = useCallback((moves: BreakoutMove[]) =>
    run(async isCurrent => {
      const actionSession = nativeRef.current ? nativeRef.current.context?.session : session
      if (!actionSession) throw new Error("No active breakout session.")
      const payload = await meetings.updateBreakoutAssignments(meetingId, actionSession.id, moves)
      if (!isCurrent()) return
      if (!nativeRef.current) setSession(payload.session)
      await refreshRef.current()
    }, "Unable to move participants."), [meetingId, run, session, setSession])

  const broadcast = useCallback((message: string) =>
    run(async () => {
      const actionSession = nativeRef.current ? nativeRef.current.context?.session : session
      if (!actionSession) throw new Error("No active breakout session.")
      await meetings.broadcastBreakoutMessage(meetingId, actionSession.id, message)
    }, "Unable to send the message."), [meetingId, run, session])

  const endSession = useCallback((delaySeconds = 0) =>
    run(async isCurrent => {
      const actionSession = nativeRef.current ? nativeRef.current.context?.session : session
      if (!actionSession) return
      const payload = await meetings.endBreakoutSession(meetingId, actionSession.id, delaySeconds)
      if (!isCurrent()) return
      if (nativeRef.current) { await nativeRef.current.refresh(); return }
      if (delaySeconds > 0 && payload.session && payload.session.status === "active") {
        setSession(payload.session)
        return
      }
      epochRef.current += 1
      endedSessionIdsRef.current.add(actionSession.id)
      setSession(null)
      setPendingInvite(null)
      if (locationRef.current.kind === "room" || movingRef.current) await returnToMain()
    }, "Unable to end breakout rooms."), [meetingId, returnToMain, run, session, setSession])

  const endsAt = useMemo(() => {
    const parsed = session?.scheduled_end_at ? Date.parse(session.scheduled_end_at) : Number.NaN
    return Number.isFinite(parsed) ? parsed : null
  }, [session?.scheduled_end_at])

  // When the countdown runs out, the host's client ends the session for everyone (the server
  // also ends it lazily on the next request, so no worker is needed).
  const endSessionRef = useRef(endSession)
  endSessionRef.current = endSession
  useEffect(() => {
    if (!isHost || endsAt === null) return
    const delay = Math.max(0, endsAt - Date.now())
    const timer = window.setTimeout(() => {
      if (nativeRef.current) void nativeRef.current.refresh()
      else void endSessionRef.current(0)
    }, delay)
    return () => window.clearTimeout(timer)
  }, [endsAt, isHost])

  const observedContext = nativeObservation?.context || null
  const observedRoom = observedContext && ['breakout', 'return_required'].includes(observedContext.status) ? observedContext.room : null
  const observedEnd = observedContext?.session?.scheduled_end_at ? Date.parse(observedContext.session.scheduled_end_at) : null
  return useMemo(() => ({
    session: nativeObservation ? observedContext?.session || null : session,
    location: nativeObservation ? (observedRoom ? { kind: 'room' as const, roomId: observedRoom.id, roomName: observedRoom.name } : { kind: 'main' as const }) : location,
    endsAt: nativeObservation ? observedEnd : endsAt,
    pendingInvite: nativeObservation ? (observedContext?.assignment?.room_id !== observedRoom?.id ? observedContext?.assignment || null : null) : pendingInvite,
    busy: busy || Boolean(nativeObservation && nativeObservation.status !== 'verified'),
    nativePresence: Boolean(nativeObservation),
    presenceVerified: nativeObservation?.status === 'verified',
    returnError: returnFailure?.scope === nativeObservation?.scope ? returnFailure?.message : null,
    error: nativeObservation && movementFailure?.scope === nativeObservation.scope ? movementFailure.message : error,
    isHost,
    refresh,
    joinPendingInvite,
    joinRoomAsHost,
    returnToMain,
    startSession,
    moveMembers,
    broadcast,
    endSession,
  }), [movementFailure, returnFailure, nativeObservation, observedContext, observedRoom, observedEnd, broadcast, busy, endSession, endsAt, error, isHost, joinPendingInvite, joinRoomAsHost, location, moveMembers, pendingInvite, refresh, returnToMain, session, startSession])
}
